import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMe } from "../../api/hooks";
import { ChatView } from "../../components/chat/ChatView";
import { jumpState } from "../../lib/jump";
import { useUi } from "../../state/ui";

type Search = { thread?: string; m?: string };

export const Route = createFileRoute("/_app/c/$id")({
  // A personal chat, a DM or an inbox conversation; `thread` opens a DM thread (PLAN §6.5).
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(typeof search.thread === "string" && search.thread ? { thread: search.thread } : {}),
    ...(typeof search.m === "string" && search.m ? { m: search.m } : {}),
  }),
  component: ChatPage,
});

function ChatPage() {
  const { id } = Route.useParams();
  const { thread, m } = Route.useSearch();
  // A draft handed over by another screen ("Set up with Vantage"), used once.
  const draft = useUi((s) => s.draft);
  const setDraft = useUi((s) => s.setDraft);
  // This page survives /c/A → /c/B, so the draft stays tied to its own chat (review R3-4),
  // and it's gone once we leave that chat: coming back doesn't type it again (R3r2-10).
  const [q, setQ] = useState(() => (draft?.conversationId === id ? draft : null));
  useEffect(() => {
    if (q !== null) setDraft(null);
  }, [q, setDraft]);
  useEffect(() => {
    if (q !== null && q.conversationId !== id) setQ(null);
  }, [q, id]);
  const navigate = useNavigate();
  const me = useMe();
  if (!me.data) return null;
  return (
    <ChatView
      key={id}
      conversationId={id}
      me={me.data}
      thread={thread ?? null}
      jumpTo={m ?? null}
      initialText={q?.conversationId === id ? q.text : undefined}
      onThread={(root) =>
        void navigate({
          to: "/c/$id",
          params: { id },
          search: root ? { thread: root } : {},
          replace: Boolean(thread && root),
        })
      }
      onTarget={(root, message, push) =>
        void navigate({
          to: "/c/$id",
          params: { id },
          search: { ...(root ? { thread: root } : {}), ...(message ? { m: message } : {}) },
          // A search hit is a followed link: it goes to its message again even when that's the
          // one open (R10-B1), replacing the entry then (nothing new to go Back to).
          replace: !push || ((root ?? undefined) === thread && (message ?? undefined) === m),
          ...(push ? { state: jumpState() } : {}),
        })
      }
    />
  );
}
